import { GetObjectCommand, type S3Client } from '@aws-sdk/client-s3'
import { OmfError, parseOmf, sha256Hex, type ParsedOmf } from '../omf'
import {
  getJsonObject,
  isNotFoundError,
  paginateList,
  UPLOAD_BUCKET,
  withS3Client,
  type S3Io,
} from './s3'
import {
  compareSemver,
  getVersionsIndex,
  isSemver,
  latestVersionOf,
  listVersionFolders,
  maxSemver,
  resolveLiveVersion,
  VERSIONS_INDEX_FILENAME,
} from './toyVersions'

const METADATA_FILENAME = 'metadata.json'
const MANIFEST_EXT = '.omf'
const SHA_CONCURRENCY = 4

// Admin-side deep inspection of one toy: everything actually in the toy's
// content folder cross-checked against everything its OMF manifest claims,
// including SHA-256 verification of the file bytes.

export type FilePresence = 'both' | 'folder-only' | 'manifest-only'

export type ShaStatus =
  // In both folder and manifest with a checksum — verifiable via verifyToyShas.
  | 'pending'
  | 'match'
  | 'mismatch'
  // Not verifiable: missing on one side, or the manifest carries no checksum.
  | 'not-checked'

export type ToyFileRow = {
  // Path relative to the toy's content folder (the version folder for
  // versioned toys, the toy root for legacy ones).
  name: string
  presence: FilePresence
  size?: number
  manifestSha?: string
  actualSha?: string
  shaStatus: ShaStatus
}

export type ToyInspection = {
  // Version folder that was inspected; null for a legacy un-versioned toy.
  version: string | null
  metadata: Record<string, unknown> | null
  manifestName: string | null
  appName: string | null
  files: ToyFileRow[]
  problems: string[]
}

// Which folder holds the toy's current content: the latest version folder
// (per versions-index, else highest semver folder on disk), or the legacy toy
// root when no version folders exist.
async function resolveContentVersion(
  client: S3Client,
  identityId: string,
  toyId: string,
): Promise<string | null> {
  const index = await getVersionsIndex(client, identityId, toyId)
  const latest = index ? latestVersionOf(index) : null
  if (latest) return latest
  return maxSemver(await listVersionFolders(client, identityId, toyId))
}

async function listContentFiles(
  client: S3Client,
  identityId: string,
  toyId: string,
  version: string | null,
): Promise<Array<{ name: string; size: number }>> {
  const prefix = version
    ? `${identityId}/${toyId}/${version}/`
    : `${identityId}/${toyId}/`
  const files: Array<{ name: string; size: number }> = []
  for await (const res of paginateList(client, { Bucket: UPLOAD_BUCKET, Prefix: prefix })) {
    for (const o of res.Contents ?? []) {
      if (!o.Key) continue
      const name = o.Key.slice(prefix.length)
      if (!name) continue
      if (!version) {
        // Legacy root: don't mix in the versioned layout's files.
        if (name === VERSIONS_INDEX_FILENAME) continue
        const slash = name.indexOf('/')
        if (slash > 0 && isSemver(name.slice(0, slash))) continue
      }
      files.push({ name, size: o.Size ?? 0 })
    }
  }
  return files
}

async function getText(client: S3Client, key: string): Promise<string | null> {
  try {
    const res = await client.send(new GetObjectCommand({ Bucket: UPLOAD_BUCKET, Key: key }))
    return res.Body ? await res.Body.transformToString() : null
  } catch (err) {
    if (isNotFoundError(err)) return null
    throw err
  }
}

// Presence-level inspection: one listing plus the metadata and manifest reads.
// Cheap enough to run on page load; byte-level SHA checks are a separate,
// heavier step (verifyToyShas) since they download every file.
//
// pinnedVersion selects which folder to inspect: omit it to auto-resolve the
// toy's current version, pass a version string for a specific (possibly
// staged) version folder, or null for the legacy un-versioned toy root.
export async function inspectToy(
  io: S3Io,
  identityId: string,
  toyId: string,
  pinnedVersion?: string | null,
): Promise<ToyInspection> {
  return withS3Client(io, async (client) => {
    const problems: string[] = []
    const version =
      pinnedVersion === undefined
        ? await resolveContentVersion(client, identityId, toyId)
        : pinnedVersion
    const folderFiles = await listContentFiles(client, identityId, toyId, version)
    const contentPrefix = version
      ? `${identityId}/${toyId}/${version}/`
      : `${identityId}/${toyId}/`

    let metadata: Record<string, unknown> | null = null
    const metadataResult = await getJsonObject(
      client,
      UPLOAD_BUCKET,
      `${contentPrefix}${METADATA_FILENAME}`,
    )
    if (metadataResult.kind === 'ok') metadata = metadataResult.value
    else if (metadataResult.kind === 'missing') problems.push('No metadata.json in the toy folder')
    else if (metadataResult.kind === 'invalid-json') problems.push('metadata.json is not valid JSON')
    else problems.push('metadata.json is not a JSON object')

    // Prefer the manifest named by the metadata; fall back to any .omf file.
    const folderNames = new Set(folderFiles.map((f) => f.name))
    const namedManifest =
      typeof metadata?.manifestFilename === 'string' ? metadata.manifestFilename : null
    const manifestName =
      namedManifest && folderNames.has(namedManifest)
        ? namedManifest
        : (folderFiles.find((f) => f.name.toLowerCase().endsWith(MANIFEST_EXT))?.name ?? null)
    if (namedManifest && manifestName !== namedManifest) {
      problems.push(`metadata names manifest ${namedManifest} but it is not in the folder`)
    }

    let manifest: ParsedOmf | null = null
    if (manifestName) {
      const manifestText = await getText(client, `${contentPrefix}${manifestName}`)
      if (manifestText !== null) {
        try {
          manifest = parseOmf(manifestText)
        } catch (err) {
          problems.push(
            err instanceof OmfError ? `Manifest: ${err.message}` : 'Manifest could not be parsed',
          )
        }
      }
    } else {
      problems.push('No .omf manifest in the toy folder')
    }

    const manifestByName = new Map(
      (manifest?.entries ?? []).map((e) => [e.serverFilename, e]),
    )
    const names = [...new Set([...folderNames, ...manifestByName.keys()])].sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase()),
    )
    const files: ToyFileRow[] = names.map((name) => {
      const folderFile = folderFiles.find((f) => f.name === name)
      const entry = manifestByName.get(name)
      const presence: FilePresence = folderFile
        ? entry
          ? 'both'
          : 'folder-only'
        : 'manifest-only'
      return {
        name,
        presence,
        size: folderFile?.size,
        manifestSha: entry?.sha256,
        shaStatus: presence === 'both' && entry?.sha256 ? 'pending' : 'not-checked',
      }
    })

    return {
      version,
      metadata,
      manifestName,
      appName: manifest?.appName ?? null,
      files,
      problems,
    }
  })
}

// One version's metadata.json (null when missing/unreadable — the inspection
// panel reports corruption in detail). version null reads the legacy
// un-versioned toy root.
export async function loadVersionMetadata(
  io: S3Io,
  identityId: string,
  toyId: string,
  version: string | null,
): Promise<Record<string, unknown> | null> {
  return withS3Client(io, async (client) => {
    const prefix = version
      ? `${identityId}/${toyId}/${version}/`
      : `${identityId}/${toyId}/`
    const result = await getJsonObject(client, UPLOAD_BUCKET, `${prefix}${METADATA_FILENAME}`)
    return result.kind === 'ok' ? result.value : null
  })
}

export type ToyVersionSummary = {
  version: string
  // Whether versions-index.json names this the live version. Staged versions
  // (folder exists, index still points elsewhere) report false.
  isLatest: boolean
  createdAt?: string
  metadata: Record<string, unknown> | null
}

// All version folders of a toy, newest first, annotated with which one is
// live and each version's metadata.json (null when missing/corrupt — an
// incomplete or mid-upload version). Legacy toys (no version folders)
// return [].
export async function listToyVersionSummaries(
  io: S3Io,
  identityId: string,
  toyId: string,
): Promise<ToyVersionSummary[]> {
  return withS3Client(io, async (client) => {
    const [index, folders] = await Promise.all([
      getVersionsIndex(client, identityId, toyId),
      listVersionFolders(client, identityId, toyId),
    ])
    if (folders.length === 0) return []
    const latest = resolveLiveVersion(index, folders)
    const createdAt = new Map<string, string>()
    for (const entry of index?.versions ?? []) {
      if (typeof entry.createdAt === 'string') createdAt.set(entry.version, entry.createdAt)
    }
    const sorted = [...folders].sort((a, b) => compareSemver(b, a))
    return Promise.all(
      sorted.map(async (version): Promise<ToyVersionSummary> => {
        // Corrupt metadata reads as an incomplete version in the listing.
        const result = await getJsonObject(
          client,
          UPLOAD_BUCKET,
          `${identityId}/${toyId}/${version}/${METADATA_FILENAME}`,
        )
        const metadata = result.kind === 'ok' ? result.value : null
        // metadata.creationTime is stamped by creation/upgrade flows and is
        // the authoritative timestamp; the versions-index createdAt (e.g.
        // derived from LastModified during migration) covers older versions.
        const creationTime =
          typeof metadata?.creationTime === 'string' ? metadata.creationTime : undefined
        return {
          version,
          isLatest: version === latest,
          createdAt: creationTime ?? createdAt.get(version),
          metadata,
        }
      }),
    )
  })
}

// Downloads every 'pending' file and checks its SHA-256 against the manifest.
// onRow fires as each file finishes so the UI can update progressively.
export async function verifyToyShas(
  io: S3Io,
  identityId: string,
  toyId: string,
  inspection: ToyInspection,
  onRow: (row: ToyFileRow) => void,
): Promise<void> {
  const contentPrefix = inspection.version
    ? `${identityId}/${toyId}/${inspection.version}/`
    : `${identityId}/${toyId}/`
  const pending = inspection.files.filter((f) => f.shaStatus === 'pending')
  await withS3Client(io, async (client) => {
    for (let i = 0; i < pending.length; i += SHA_CONCURRENCY) {
      const chunk = pending.slice(i, i + SHA_CONCURRENCY)
      await Promise.all(
        chunk.map(async (row) => {
          const res = await client.send(
            new GetObjectCommand({ Bucket: UPLOAD_BUCKET, Key: `${contentPrefix}${row.name}` }),
          )
          const bytes = res.Body ? await res.Body.transformToByteArray() : new Uint8Array()
          const actualSha = await sha256Hex(bytes)
          onRow({
            ...row,
            actualSha,
            shaStatus: actualSha === row.manifestSha ? 'match' : 'mismatch',
          })
        }),
      )
    }
  })
}
